import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Map as MapboxMap, Source } from 'react-map-gl/mapbox'
import type { MapMouseEvent, MapRef } from 'react-map-gl/mapbox'
import { NavigationControl } from 'mapbox-gl'
import { useMantineColorScheme, useMantineTheme } from '@mantine/core'
import type { FeatureCollection, LineString, Point } from 'geojson'
import 'mapbox-gl/dist/mapbox-gl.css'

import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { FULL_UPLOAD_POINT_LIMIT, PLAY_INTERVAL_MS } from './constants'
import type { DocPoint } from './features'
import {
  fitBoundsFor,
  headingTip,
  isPointInBounds,
  pointsToCollection,
  trackLine,
  viewportBounds,
  viewportFeatureIndexes,
  type LngLatBounds,
} from './points'
import { useLatest } from './useLatest'

const EMPTY_POINTS: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] }
const EMPTY_LINE: FeatureCollection<LineString> = { type: 'FeatureCollection', features: [] }

const hitPosition = (event: MapMouseEvent): number | null => {
  const raw = event.features?.[0]?.properties?.position
  const position = typeof raw === 'number' ? raw : Number(raw)
  return Number.isInteger(position) ? position : null
}

const DocumentationMap = ({
  points,
  visible,
  cursor,
  follow,
  satellite,
  onSelect,
}: {
  points: DocPoint[]
  visible: number[]
  cursor: number
  follow: boolean
  satellite: boolean
  onSelect: (position: number) => void
}) => {
  const mapRef = useRef<MapRef>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const zoomControl = useRef<NavigationControl | null>(null)
  const fitted = useRef<number[] | null>(null)
  const pointsRef = useLatest(points)
  const visibleRef = useLatest(visible)
  const theme = useMantineTheme()
  const { colorScheme } = useMantineColorScheme()
  const [bounds, setBounds] = useState<LngLatBounds | null>(null)
  const cull = points.length > FULL_UPLOAD_POINT_LIMIT
  const renderedIndexes = useMemo(
    () => viewportFeatureIndexes(points, visible, bounds, cull),
    [bounds, cull, points, visible]
  )
  const rendered = useMemo(() => {
    const allowed = new Set(renderedIndexes)
    return visible.flatMap((featureIndex, position) => (allowed.has(featureIndex) ? [{ featureIndex, position }] : []))
  }, [renderedIndexes, visible])
  const collection = useMemo(() => pointsToCollection(points, rendered), [points, rendered])
  const line = useMemo(() => trackLine(points, rendered), [points, rendered])
  const selected = cursor >= 0 && cursor < visible.length ? points[visible[cursor]] : null
  const selectedCollection = useMemo<FeatureCollection<Point>>(() => {
    if (!selected) return EMPTY_POINTS
    return pointsToCollection(points, [{ featureIndex: visible[cursor], position: cursor }])
  }, [cursor, points, selected, visible])
  const heading = useMemo<FeatureCollection<LineString>>(() => {
    if (!selected) return EMPTY_LINE
    const tip = headingTip(selected)
    if (!tip) return EMPTY_LINE
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [[selected.lon, selected.lat], tip] },
        },
      ],
    }
  }, [selected])
  const mapStyle = satellite
    ? 'mapbox://styles/mapbox/satellite-streets-v12'
    : `mapbox://styles/mapbox/${colorScheme === 'dark' ? 'dark-v11' : 'streets-v12'}`

  const fitTrack = useCallback(() => {
    const map = mapRef.current
    const next = fitBoundsFor(pointsRef.current, visibleRef.current)
    const container = map?.getContainer()
    if (!map || !next || !container || container.clientWidth < 2 || container.clientHeight < 2) return
    const camera = map.cameraForBounds(next, { padding: 16, maxZoom: 18 })
    if (!camera || camera.zoom == null || !Number.isFinite(camera.zoom)) return
    map.jumpTo({
      center: camera.center,
      zoom: Math.min(camera.zoom + 1, 19),
      bearing: camera.bearing,
      pitch: camera.pitch,
    })
    fitted.current = visibleRef.current
    setBounds(viewportBounds(map))
  }, [pointsRef, visibleRef])

  const onLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (map && !zoomControl.current) {
      zoomControl.current = new NavigationControl({ showCompass: false })
      map.addControl(zoomControl.current, 'top-right')
    }
    fitTrack()
  }, [fitTrack])

  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    const observer = new ResizeObserver(() => {
      const map = mapRef.current
      if (!map) return
      const { width, height } = frame.getBoundingClientRect()
      if (width < 2 || height < 2) return
      map.resize()
      if (fitted.current !== visibleRef.current) fitTrack()
    })
    observer.observe(frame)
    return () => observer.disconnect()
  }, [fitTrack, visibleRef])

  useEffect(() => {
    if (fitted.current === visible) return
    fitTrack()
  }, [fitTrack, points, visible])

  useEffect(() => {
    if (!follow || !selected) return
    const map = mapRef.current
    const inner = map ? viewportBounds(map, -0.2) : null
    if (!map || !inner || isPointInBounds(selected, inner)) return
    map.easeTo({ center: [selected.lon, selected.lat], duration: PLAY_INTERVAL_MS })
  }, [follow, selected])

  return (
    <div ref={frameRef} style={{ position: 'absolute', inset: 0 }}>
      <MapboxMap
        ref={mapRef}
        mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
        mapStyle={mapStyle}
        initialViewState={{ longitude: 14.42, latitude: 50.08, zoom: 13 }}
        onLoad={onLoad}
        maxZoom={20}
        interactiveLayerIds={['documentation-points', 'documentation-points-hit']}
        onClick={event => {
          const position = hitPosition(event)
          if (position != null) onSelect(position)
        }}
        onMoveEnd={event => {
          if (cull) setBounds(viewportBounds(event.target))
        }}
        style={{ width: '100%', height: '100%' }}
      >
        <Source id="documentation-track" type="geojson" data={line}>
          <Layer
            id="documentation-track-line"
            type="line"
            paint={{ 'line-color': theme.colors.blue[5], 'line-width': 2, 'line-opacity': 0.45 }}
          />
        </Source>
        <Source id="documentation-points" type="geojson" data={collection}>
          <Layer id="documentation-points-hit" type="circle" paint={{ 'circle-radius': 12, 'circle-opacity': 0.01 }} />
          <Layer
            id="documentation-points"
            type="circle"
            paint={{
              'circle-radius': 4,
              'circle-color': ['case', ['==', ['get', 'hasPhoto'], 1], theme.colors.blue[6], theme.colors.gray[6]],
              'circle-stroke-width': 1,
              'circle-stroke-color': theme.colors.dark[9],
            }}
          />
        </Source>
        <Source id="documentation-heading" type="geojson" data={heading}>
          <Layer
            id="documentation-heading-line"
            type="line"
            paint={{ 'line-color': theme.colors.orange[6], 'line-width': 3 }}
          />
        </Source>
        <Source id="documentation-selected" type="geojson" data={selectedCollection}>
          <Layer
            id="documentation-selected-point"
            type="circle"
            paint={{
              'circle-radius': 8,
              'circle-color': theme.colors.orange[6],
              'circle-opacity': 0.45,
              'circle-stroke-width': 3,
              'circle-stroke-color': theme.colors.orange[6],
            }}
          />
        </Source>
      </MapboxMap>
    </div>
  )
}

export default DocumentationMap
